import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { Bookmark, Heart, MapPin, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Favorites() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/favorites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Removed"));
      setItems((p) => p.filter((x) => x.attraction_id !== id));
    } catch (e) { toast.error(t("فشل الحذف", "Remove failed")); }
  };

  return (
    <DashboardLayout title={t("الأماكن المفضلة", "Favorite Places")}>
      <div className="space-y-6" data-testid="favorites-page">
        <div className="relative glass-card rounded-3xl p-6 lg:p-8 bg-luxury overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-yellow-500/20 blur-[100px] rounded-full" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-500/20 blur-[100px] rounded-full" />
          <div className="relative flex items-center justify-between gap-4">
            <div>
              <Badge className="bg-yellow-500/20 text-yellow-400 mb-2"><Heart size={12} className="ml-1" /> {t("مفضلتك", "Your Favorites")}</Badge>
              <h2 className="text-2xl lg:text-3xl font-bold text-luxury">{t("الأماكن التي حفظتها", "Places you've saved")}</h2>
              <p className="text-slate-400 text-sm mt-1">{t("ارجع إليها في أي وقت لتخطيط زياراتك القادمة", "Come back to them anytime to plan your next visits")}</p>
            </div>
            <Link to="/discover">
              <Button variant="outline" className="border-slate-700 text-white">{t("استكشف المزيد", "Discover more")}</Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map((i) => <div key={i} className="glass-card rounded-2xl h-72 skeleton" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Bookmark className="text-slate-600 mx-auto mb-3" size={40} />
            <p className="text-slate-400 mb-4">{t("لم تحفظ أي أماكن بعد", "No favorites yet")}</p>
            <Link to="/discover"><Button className="btn-primary">{t("ابدأ الاستكشاف", "Start discovering")}</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((a) => (
              <div key={a.attraction_id} className="glass-card rounded-2xl overflow-hidden purple-gold-glow" data-testid={`fav-${a.attraction_id}`}>
                <button onClick={() => navigate(`/discover/${a.attraction_id}`)} className="w-full text-right">
                  <div className="relative h-44 overflow-hidden bg-slate-900">
                    <ListingImage path={a.images?.[0]} alt={a.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                    {a.rating && (
                      <div className="absolute top-3 left-3 bg-yellow-500/90 text-black text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Star size={12} fill="currentColor" /> {a.rating}
                      </div>
                    )}
                    <div className="absolute bottom-3 right-3 left-3 text-white">
                      <h3 className="font-bold text-lg leading-tight">{language === "ar" ? (a.name_ar || a.name) : a.name}</h3>
                      <p className="text-xs text-slate-300 flex items-center gap-1 mt-1"><MapPin size={12} className="text-yellow-500" />{a.city}, {a.state}</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-400 line-clamp-2 text-right">{a.description}</p>
                  </div>
                </button>
                <div className="px-4 pb-4 border-t border-slate-800 pt-3 flex justify-end">
                  <button onClick={() => remove(a.attraction_id)} className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1" data-testid={`remove-fav-${a.attraction_id}`}>
                    <Trash2 size={14} /> {t("إزالة من المفضلة", "Remove")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
